import React, { ReactElement, useContext, useEffect, useMemo, useState } from 'react';

import { FormProvider } from 'react-hook-form';
import isEqual from 'lodash/isEqual';
import cloneDeep from 'lodash/cloneDeep';

import { SaveButton } from '../views/profile/components/SaveButton';
import { setFormValues } from '../utils';
import { RouterPrompt } from './RouterPrompt';
import { DeviceContext } from '../contexts';

interface Props {
  methods: any;
  initialData?: any;
  handleSubmit: Function;
  children?: ReactElement;
  loading?: boolean;
  prePopulateValues?: boolean;
  isDisabled?: boolean;
}

let originalPersonalInfoData: any;

export const AppFormWrapper: React.FC<Props> = ({
  prePopulateValues = true,
  methods,
  initialData,
  handleSubmit,
  loading,
  children,
  isDisabled = false,
}) => {
  const {
    getValues,
    setValue,
    watch,
    formState: { isDirty },
  } = methods;
  const { isMobile } = useContext(DeviceContext);
  const [showChangeButton, setShowChangeButton] = useState<boolean>(false);

  useEffect(() => {
    if (initialData && prePopulateValues) {
      const oldValues = getValues();
      setFormValues(initialData, setValue, oldValues);
    }
    originalPersonalInfoData = cloneDeep(getValues());
    const subscription = watch((values: any) => {
      if (values) {
        const isChanged = !isEqual(values, originalPersonalInfoData);
        setShowChangeButton(isChanged);
      }
    });
    return () => subscription.unsubscribe();
  }, [initialData, watch]);

  useEffect(() => {
    setShowChangeButton(isDirty);
  }, [isDirty]);

  useEffect(() => {
    setShowChangeButton(false);
  }, [initialData]);

  return (
    <>
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(handleSubmit)}>
          {children}
          {showChangeButton && <SaveButton loading={loading} isDisabled={isDisabled} />}
        </form>
      </FormProvider>

      <RouterPrompt
        when={showChangeButton}
        title="Before you go..."
        subtitle="Do you want to save the changes you've made?"
        okText={isMobile ? 'Yes' : 'Yes, save my changes'}
        cancelText={isMobile ? 'No' : 'No, ignore my changes'}
        onOK={async () => {
          await methods.handleSubmit(handleSubmit)();
          return true;
        }}
        onCancel={() => true}
      />
    </>
  );
};
